import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ToolbarModule } from 'primeng/toolbar';
import { DividerModule } from 'primeng/divider';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ComplaintService, ComplaintModel, ImageModel } from '../../services/complaint.service';

@Component({
  selector: 'app-complaint-details',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    TagModule,
    ToolbarModule,
    DividerModule,
    DialogModule,
    ToastModule,
  ],
  providers: [MessageService],
  template: `
    <p-card *ngIf="complaint; else loadingTpl">
      <ng-template pTemplate="header">
        <p-toolbar>
          <ng-template pTemplate="start">
            <h2>Denúncia #{{ complaint.id }}</h2>
          </ng-template>
          <ng-template pTemplate="end">
            <p-button
              title="Analisar"
              icon="pi pi-file-edit"
              (onClick)="goToEdit()"
              severity="warn"
              [outlined]="true"
              class="mr-2">
            </p-button>
            <p-button
              title="Voltar"
              icon="pi pi-arrow-left"
              (onClick)="goBack()"
              severity="secondary"
              [outlined]="true">
            </p-button>
          </ng-template>
        </p-toolbar>
      </ng-template>

      <ng-template pTemplate="content">
        <div class="details-grid">
          <!-- Coluna Esquerda -->
          <div class="detail-section">
            <h3 class="section-title">
              <i class="pi pi-tag mr-2"></i> Informações Gerais
            </h3>
            <div class="detail-row">
              <span class="detail-label">ID Denúncia App</span>
              <span>{{ complaint.idDenunciaApp ?? '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Tipo</span>
              <p-tag
                [value]="complaint.complaintType?.description ?? '-'"
                [severity]="typeSeverity">
              </p-tag>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status</span>
              <p-tag
                [value]="complaint.complaintStatus?.description ?? '-'"
                [severity]="statusSeverity">
              </p-tag>
            </div>
            <div class="detail-row">
              <span class="detail-label">Data de Registro</span>
              <span>{{ complaint.registerDate | date:'dd/MM/yyyy' }}</span>
            </div>

            <p-divider></p-divider>

            <h3 class="section-title">
              <i class="pi pi-map-marker mr-2"></i> Localização
            </h3>
            <div class="detail-row">
              <span class="detail-label">Endereço</span>
              <span>{{ complaint.address ?? 'Não informado' }}</span>
            </div>
            <div class="detail-row" *ngIf="complaint.latitude || complaint.longitude">
              <span class="detail-label">Coordenadas</span>
              <span>{{ complaint.latitude }}, {{ complaint.longitude }}</span>
            </div>

            <p-divider></p-divider>

            <!-- Veículos Envolvidos -->
            <ng-container *ngIf="complaint.involvedList?.length">
              <h3 class="section-title">
                <i class="pi pi-car mr-2"></i> Veículos Envolvidos
              </h3>
              <div class="involved-list">
                <div *ngFor="let vehicle of complaint.involvedList; let i = index" class="involved-item">
                  <span class="involved-index">#{{ i + 1 }}</span>
                  <div class="involved-details">
                    <div class="involved-header">
                      <span *ngIf="vehicle.plateNumber || vehicle.placa" class="involved-plate">{{ vehicle.plateNumber || vehicle.placa }}</span>
                      <span *ngIf="vehicle.brandModel || vehicle.marcaModelo" class="involved-model">{{ vehicle.brandModel || vehicle.marcaModelo }}</span>
                      <span *ngIf="vehicle.color" class="involved-color" [style.background]="vehicle.color" [title]="vehicle.color"> </span>
                    </div>
                    <div class="involved-meta" *ngIf="vehicle.yearOfManufacture || vehicle.yearOfModel">
                      <span *ngIf="vehicle.yearOfManufacture">Fab: {{ vehicle.yearOfManufacture }}</span>
                      <span *ngIf="vehicle.yearOfModel"> • Mod: {{ vehicle.yearOfModel }}</span>
                    </div>
                    <div class="involved-owners" *ngIf="vehicle.owners?.length">
                      <span class="owners-label">Proprietário(s):</span>
                      <span *ngFor="let owner of vehicle.owners; let last = last">
                        {{ owner.name }}<span *ngIf="owner.document"> ({{ owner.document }})</span><span *ngIf="!last">, </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <p-divider></p-divider>
            </ng-container>

            <h3 class="section-title">
              <i class="pi pi-comment mr-2"></i> Observação
            </h3>
            <div class="observation-box">
              {{ complaint.text ?? 'Nenhuma observação registrada.' }}
            </div>
          </div>

          <!-- Coluna Direita: Imagens (imageList + foto1/foto2) -->
          <div class="detail-section">
            <h3 class="section-title">
              <i class="pi pi-image mr-2"></i> Imagens
              <span class="image-count">({{ totalImageCount }})</span>
            </h3>

            <ng-container *ngIf="totalImageCount > 0; else noImagesTpl">
              <div class="single-thumb-container">
                <div
                  class="image-thumb image-thumb-single"
                  (click)="openCarousel(0)"
                  title="Clique para visualizar todas as imagens">
                  <img
                    *ngIf="allImages[0]?.url as src; else loadingMiniTpl"
                    [src]="src"
                    alt="Primeira imagem" />
                  <ng-template #loadingMiniTpl>
                    <div class="thumb-placeholder">
                      <i class="pi pi-spin pi-spinner"></i>
                    </div>
                  </ng-template>
                  <div class="image-overlay">
                    <i class="pi pi-images"></i>
                    <span class="image-count-badge" *ngIf="totalImageCount > 1">
                      +{{ totalImageCount - 1 }}
                    </span>
                  </div>
                </div>
              </div>
            </ng-container>
            <ng-template #noImagesTpl>
              <p class="no-data">Nenhuma imagem vinculada a esta denúncia.</p>
            </ng-template>
          </div>
        </div>

        <!-- Diálogo de visualização em carrossel -->
        <p-dialog
          [(visible)]="imageDialogVisible"
          [modal]="true"
          [style]="{ width: '90vw', maxWidth: '900px' }"
          [dismissableMask]="true"
          [header]="'Imagem ' + (carouselIndex + 1) + ' de ' + (complaint?.imageList?.length ?? 0)"
          (onHide)="closeCarousel()">
          <ng-container *ngIf="carouselImages.length; else loadingDialogTpl">
            <div class="carousel-container">
              <!-- Botão anterior -->
              <button
                class="carousel-nav carousel-prev"
                (click)="prevImage()"
                [disabled]="carouselIndex === 0"
                title="Imagem anterior">
                <i class="pi pi-chevron-left"></i>
              </button>

              <!-- Imagem atual -->
              <div class="image-viewer">
                <img
                  *ngIf="carouselImages[carouselIndex]?.url as url; else loadingCarouselTpl"
                  [src]="url"
                  [alt]="'Imagem ampliada ' + (carouselIndex + 1)" />
                <ng-template #loadingCarouselTpl>
                  <div class="loading-container">
                    <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
                    <p>Carregando imagem...</p>
                  </div>
                </ng-template>
              </div>

              <!-- Botão próximo -->
              <button
                class="carousel-nav carousel-next"
                (click)="nextImage()"
                [disabled]="carouselIndex >= carouselImages.length - 1"
                title="Próxima imagem">
                <i class="pi pi-chevron-right"></i>
              </button>
            </div>

            <!-- Indicadores (bolinhas) -->
            <div class="carousel-dots" *ngIf="carouselImages.length > 1">
              <span
                *ngFor="let img of carouselImages; let i = index"
                class="carousel-dot"
                [class.active]="i === carouselIndex"
                (click)="openCarousel(i)">
              </span>
            </div>
          </ng-container>
          <ng-template #loadingDialogTpl>
            <div class="loading-container">
              <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
              <p>Carregando imagens...</p>
            </div>
          </ng-template>
        </p-dialog>
      </ng-template>
    </p-card>

    <ng-template #loadingTpl>
      <div class="loading-container">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p>Carregando detalhes da denúncia...</p>
      </div>
    </ng-template>

    <p-toast></p-toast>
  `,
  styles: [`
    .details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
    }

    .detail-section {
      min-width: 0;
    }

    .section-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--primary-color, #3b82f6);
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
    }

    .detail-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem 0;
      border-bottom: 1px solid var(--surface-border, #e5e7eb);
      gap: 1rem;
    }

    .detail-label {
      font-weight: 600;
      color: var(--text-color-secondary, #6b7280);
      white-space: nowrap;
    }

    .observation-box {
      background: var(--surface-ground, #f9fafb);
      border: 1px solid var(--surface-border, #e5e7eb);
      border-radius: 8px;
      padding: 1rem;
      min-height: 120px;
      white-space: pre-wrap;
      word-break: break-word;
      color: var(--text-color, #374151);
      line-height: 1.6;
    }

    /* Veículos envolvidos */
    .involved-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .involved-item {
      display: flex;
      gap: 0.75rem;
      padding: 0.75rem;
      background: var(--surface-ground, #f9fafb);
      border-radius: 6px;
      border: 1px solid var(--surface-border, #e5e7eb);
    }

    .involved-index {
      font-weight: 700;
      font-size: 0.85rem;
      color: var(--primary-color, #3b82f6);
      min-width: 28px;
      flex-shrink: 0;
    }

    .involved-details {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      flex: 1;
    }

    .involved-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .involved-plate {
      font-family: monospace;
      font-weight: 600;
      background: var(--surface-100, #e5e7eb);
      padding: 0.15rem 0.5rem;
      border-radius: 4px;
      font-size: 0.9rem;
    }

    .involved-model {
      color: var(--text-color, #374151);
      font-size: 0.9rem;
      font-weight: 500;
    }

    .involved-color {
      display: inline-block;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 1px solid var(--surface-border, #d1d5db);
      flex-shrink: 0;
    }

    .involved-meta {
      font-size: 0.8rem;
      color: var(--text-color-secondary, #6b7280);
    }

    .involved-owners {
      font-size: 0.8rem;
      color: var(--text-color-secondary, #6b7280);
    }

    .owners-label {
      font-weight: 600;
      margin-right: 0.25rem;
    }

    .image-count {
      font-size: 0.85rem;
      color: var(--text-color-secondary, #6b7280);
      font-weight: 400;
      margin-left: 0.25rem;
    }

    .image-gallery {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 0.75rem;
    }

    .image-thumb {
      position: relative;
      aspect-ratio: 1;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;
      border: 2px solid var(--surface-border, #e5e7eb);
      transition: border-color 0.2s, transform 0.2s;
      background: var(--surface-ground, #f3f4f6);
    }

    .image-thumb:hover {
      border-color: var(--primary-color, #3b82f6);
      transform: scale(1.03);
    }

    .image-thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .thumb-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-color-secondary, #9ca3af);
      font-size: 1.25rem;
    }

    .image-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;
      color: #fff;
      font-size: 1.5rem;
    }

    .image-thumb:hover .image-overlay {
      opacity: 1;
    }

        /* ===== CARROSSEL ===== */
    .carousel-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 400px;
      width: 100%;
      background: var(--surface-ground, #f9fafb);
      border-radius: 4px;
    }

    .image-viewer {
      display: flex;
      align-items: center;
      justify-content: center;
      max-height: 70vh;
      overflow: auto;
    }

    .image-viewer img {
      max-width: 100%;
      max-height: 70vh;
      object-fit: contain;
    }

     /* Navegação do carrossel */
    .carousel-nav {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
      background: rgba(0, 0, 0, 0.6);
      color: white;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      padding: 0;
    }

       .carousel-nav:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.8);
      transform: translateY(-50%) scale(1.1);
    }

    .carousel-nav:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .carousel-prev {
      left: 10px;
    }

    .carousel-next {
      right: 10px;
    }

    .carousel-nav i {
      font-size: 1.5rem;
    }

    /* Indicadores (bolinhas) */
    .carousel-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin-top: 16px;
      padding: 8px 0;
    }

    .carousel-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #ccc;
      cursor: pointer;
      transition: all 0.3s ease;
      border: none;
      padding: 0;
    }

    .carousel-dot.active {
      background: var(--primary-color, #3b82f6);
      transform: scale(1.2);
    }

    .carousel-dot:hover {
      background: #999;
    }

    .no-data {
      color: var(--text-color-secondary, #6b7280);
      font-style: italic;
      text-align: center;
      padding: 2rem 0;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 4rem;
      color: var(--text-color-secondary, #6b7280);
      gap: 1rem;
    }

    @media (max-width: 768px) {
       .details-grid {
        grid-template-columns: 1fr;
      }
      
      .carousel-nav {
        width: 32px;
        height: 32px;
      }
      
      .carousel-nav i {
        font-size: 1rem;
      }
      
      .image-viewer {
        padding: 0 30px;
      }
    }

    :host ::ng-deep .p-toolbar {
      border: none;
      padding: 1rem 0;
    }
  `]
})
export class ComplaintDetailsComponent implements OnInit, OnDestroy {
  complaint: ComplaintModel | null = null;

  imageDialogVisible = false;
  carouselIndex = 0;

  /** Lista unificada de imagens: imageList + foto1/foto2 (URLs ou base64) */
  allImages: Array<{ url: string; source: string }> = [];
  /** Total de imagens (imageList + fotos) */
  get totalImageCount(): number { return this.allImages.length; }

  /** Mapa imageId → blob URL (via viewFile) */
  imageUrls = new Map<number, string>();

  /** Lista de imagens do carrossel (derivada de imageUrls + fotos) */
  carouselImages: Array<{ image?: ImageModel; url: string | null }> = [];

  /** Cores baseadas no id para manter consistência visual */
  typeSeverity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' = 'info';
  statusSeverity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' = 'warn';

  private readonly severityPalette: Array<'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast'> = [
    'success', 'info', 'warn', 'danger', 'secondary', 'contrast'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private complaintService: ComplaintService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (isNaN(id)) {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'ID inválido.' });
      this.router.navigate(['/home/complaints']);
      return;
    }
    this.loadComplaint(id);
  }

  ngOnDestroy(): void {
    // Revoga todas as blob URLs para liberar memória
    this.imageUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  loadComplaint(id: number): void {
    this.complaintService.findById(id).subscribe({
      next: (data) => {
        this.complaint = data;
        this.typeSeverity = this.severityFromId(data.complaintType?.id);
        this.statusSeverity = this.severityFromId(data.complaintStatus?.id);

        // Monta a lista unificada de imagens (imageList + foto1/foto2)
        this.buildAllImages(data);

        // Carrega os thumbnails via viewFile
        this.loadAllThumbnails(data.imageList ?? []);
      },
      error: (err) => {
        console.error('Erro ao carregar denúncia:', err);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar a denúncia.' });
        this.router.navigate(['/home/complaints']);
      }
    });
  }

  /** Constrói allImages (para thumbnail) e carouselImages (para o diálogo) */
  private buildAllImages(data: ComplaintModel): void {
    const list: Array<{ url: string; source: string }> = [];

    // Imagens do imageList (serão carregadas via viewFile)
    (data.imageList ?? []).forEach(img => {
      list.push({ url: '', source: 'imageList' });
    });

    // foto1 e foto2 (já vêm como base64 ou URL no JSON)
    if (data.foto1) {
      list.push({ url: data.foto1, source: 'foto1' });
    }
    if (data.foto2) {
      list.push({ url: data.foto2, source: 'foto2' });
    }

    this.allImages = list;

    // Inicializa o carrossel
    this.carouselImages = list.map(item => ({
      url: item.url || null
    }));
  }

  /** Carrega cada imagem do imageList via viewFile e atualiza allImages/carouselImages */
  private loadAllThumbnails(images: ImageModel[]): void {
    images.forEach((image, idx) => {
      this.complaintService.viewFile(image.id).subscribe({
        next: (blob) => {
          const url = this.complaintService.blobToImageUrl(blob);
          this.imageUrls.set(image.id, url);

          // Atualiza a posição correspondente em allImages
          if (idx < this.allImages.length) {
            this.allImages[idx].url = url;
          }
          // Atualiza no carrossel
          if (idx < this.carouselImages.length) {
            this.carouselImages[idx].url = url;
          }
        },
        error: (err) => {
          console.warn(`Erro ao carregar thumbnail da imagem ${image.id}:`, err);
        }
      });
    });
  }

  /** Abre o carrossel no índice especificado */
  openCarousel(index: number): void {
    this.carouselIndex = Math.max(0, Math.min(index, this.carouselImages.length - 1));
    this.imageDialogVisible = true;

    // Garante que a imagem atual está carregada
    this.ensureCarouselImage(this.carouselIndex);
    // Pré-carrega vizinhas
    if (this.carouselIndex > 0) this.ensureCarouselImage(this.carouselIndex - 1);
    if (this.carouselIndex < this.carouselImages.length - 1) this.ensureCarouselImage(this.carouselIndex + 1);
  }

  prevImage(): void {
    if (this.carouselIndex > 0) this.openCarousel(this.carouselIndex - 1);
  }

  nextImage(): void {
    if (this.carouselIndex < this.carouselImages.length - 1) this.openCarousel(this.carouselIndex + 1);
  }

  closeCarousel(): void {
    this.imageDialogVisible = false;
  }

  /** Carrega a imagem do carrossel se ainda não tiver URL (imageList via viewFile) */
  private ensureCarouselImage(index: number): void {
    const entry = this.carouselImages[index];
    if (!entry || entry.url) return;

    // Se for do imageList, busca via viewFile
    const img = this.complaint?.imageList?.[index];
    if (img) {
      this.complaintService.viewFile(img.id).subscribe({
        next: (blob) => {
          entry.url = this.complaintService.blobToImageUrl(blob);
          if (index < this.allImages.length) {
            this.allImages[index].url = entry.url;
          }
        },
        error: (err) => console.warn(`Erro ao carregar imagem ${img.id}:`, err)
      });
    }
  }

  goToEdit(): void {
    if (this.complaint?.id != null) {
      this.router.navigate(['/home/complaints/edit', this.complaint.id]);
    }
  }

  goBack(): void {
    this.router.navigate(['/home/complaints']);
  }

  private severityFromId(id?: number): typeof this.severityPalette[number] {
    if (id != null) {
      return this.severityPalette[id % this.severityPalette.length];
    }
    return 'info';
  }
}
